import {
  useEditShareMutation,
  useGetDemoQuery,
  useToggleShareMutation,
} from '@carboncopyco/data';
import { InfoOutlineIcon, ShareOutlineIcon } from '@carboncopyco/icons';
import type { DemoShare, IntegrationVendor } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Container,
  SkeletonRowLoading,
  TextBase,
  TextButton,
  TextCaption,
  TextTooltip,
  Tooltip,
  TooltipBubble,
  UserAvatar,
  notify,
} from '@carboncopyco/ui';
import {
  MMMDoYYYY,
  MMMDoYYYYUnix,
  addDaysUnixDate,
  dateUTCFormat,
  dateUTCFromNow,
  isFuture,
  removeUndefinedValues,
} from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { type Col, FixedTable } from '../../../FixedTable';
import {
  DEFAULT_EXPIRATION_PERIOD,
  ShareDemoFlow,
  accessDescription,
  accessIcons,
} from '../../../Share';
import type { ShareDemoModalState } from '../../../Share/ShareDemoModal';
import { useUrlState } from '../../../utils';
import { EmptyState } from '../../EmptyState';
import { TableRowWrapper } from '../../TableRowWrapper';
import { INITIAL_URL_STATE } from '../../consts';
import type { UrlState } from '../../types';
import { handleSinglePlural } from '../../utils';
import { useDemoPageProps } from '../DemoPageContext';
import { LinkActions } from './LinkActions';
import { LinkToggle } from './LinkToggle';

export const LinksTable = () => {
  const { organizationId } = useParams();
  const {
    demoId,
    goToLinksTab,
    workspaceId,
    demoLinksResponse: {
      items,
      fetchFirstBatch,
      fetchNextBatch,
      isLoading,
      isLoadingBatch,
      error,
      replaceItem,
    },
  } = useDemoPageProps();

  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const infoRef = useRef<HTMLDivElement>(null);
  const [{ sort, creators, companies, sources, query }] =
    useUrlState<UrlState>(INITIAL_URL_STATE);

  const [loadingLinkId, setLoadingLinkId] = useState<DemoShare['id']>(null);
  const [toggleShare] = useToggleShareMutation();
  const [editShare] = useEditShareMutation();

  const toggleActive = useCallback(
    (link: DemoShare) => {
      const { options, id, isActive } = link;
      const expired = options.expiresAt && !isFuture(options.expiresAt);
      if (expired) {
        setLoadingLinkId(link.id);
        editShare({
          organizationId,
          shareId: id,
          ...options,
          cover: options.welcomeMatConfig,
          expiresAt: options.daysToExpire
            ? addDaysUnixDate(options.daysToExpire)
            : addDaysUnixDate(DEFAULT_EXPIRATION_PERIOD),
        })
          .unwrap()
          .then(() => {
            toggleShare({
              organizationId,
              id: Number(id),
              active: true,
            })
              .unwrap()
              .then((response) => {
                replaceItem(id, {
                  ...(response as DemoShare),
                  isActive: true,
                });
              })
              .catch(() => {
                notify.error('Error updating share status');
              })
              .finally(() => {
                setLoadingLinkId(null);
              });
          });
        return;
      }
      const newIsActive = !isActive;
      replaceItem(id, { ...link, isActive: newIsActive });
      toggleShare({
        organizationId,
        id: Number(id),
        active: newIsActive,
      })
        .unwrap()
        .catch(() => {
          notify.error('Error updating share status');
          replaceItem(id, { ...link, isActive });
        });
    },
    [editShare, organizationId, replaceItem, toggleShare]
  );

  const handleSubmit = useCallback(
    async (values: ShareDemoModalState, link: DemoShare) => {
      setLoadingLinkId(link.id);
      const {
        linkName,
        skipWelcome,
        cover,
        expiresAt,
        daysToExpire,
        security,
        salesforceLeads,
        hubspotLeads,
        slackIntegration,
        showGuidanceTooltip,
        welcomeMatTitle,
      } = values;
      replaceItem(link.id, {
        ...link,
        options: {
          ...link.options,
          linkDescription: linkName,
          skipWelcome,
          welcomeMatConfig: cover,
          expiresAt,
          daysToExpire,
          security,
          integrations: Object.keys(
            removeUndefinedValues({
              salesforce: salesforceLeads,
              hubspot: hubspotLeads,
              slack: slackIntegration,
            })
          ) as IntegrationVendor[],
          welcomeMatTitle,
          showGuidanceTooltip,
        },
      });
      await editShare({
        linkDescription: linkName,
        skipWelcome,
        cover,
        expiresAt,
        daysToExpire,
        security,
        organizationId,
        shareId: link.id,
        integrationLeads: Object.keys(
          removeUndefinedValues({
            salesforce: salesforceLeads,
            hubspot: hubspotLeads,
            slack: slackIntegration,
          })
        ) as IntegrationVendor[],
        showGuidanceTooltip,
        welcomeMatTitle,
      })
        .unwrap()
        .then(() => {
          notify.success('Link settings were modified successfully');
        })
        .catch(() => {
          replaceItem(link.id, link);
          notify.error('Failed to edit link settings');
        })
        .finally(() => {
          setLoadingLinkId(null);
        });
    },
    [editShare, organizationId, replaceItem]
  );

  const cols = useMemo<Col<DemoShare>[]>(
    () => [
      {
        title: 'Link name',
        width: 160,
        maxContentWidth: 155,
        render: (link) => (
          <TooltipBubble
            title={
              <div className="flex flex-col px-1.5 pb-1 pt-1 leading-none">
                <TextTooltip
                  label={link.options.linkDescription}
                  color="text-ds-natural-50"
                />
                <TextTooltip
                  label={`Created on ${dateUTCFormat(link.updateTime)}`}
                  color="text-ds-natural-50"
                />
              </div>
            }
            placement="top-start"
          >
            <div className="flex flex-col text-ds-natural-600">
              <TextButton
                ellipsis
                label={link.options.linkDescription}
                color="text-ds-natural-600"
              />
              <TextCaption
                label={`${MMMDoYYYY(link.updateTime)}`}
                color="text-ds-natural-300"
                ellipsis
              />
            </div>
          </TooltipBubble>
        ),
      },
      {
        title: 'Shared by',
        alignment: 'center',
        width: 110,
        render: (link) => (
          <TooltipBubble
            title={link.creator.name}
            placement="top"
          >
            <UserAvatar
              user={link.creator}
              size="small"
            />
          </TooltipBubble>
        ),
      },
      {
        title: 'Opportunity',
        width: 113,
        maxContentWidth: 100,
        render: (link) => {
          const internalClientNames = ['internal', 'anyone'];
          const opportunity = internalClientNames.includes(
            link.entityMetadata?.forClient
          )
            ? ''
            : link.entityMetadata?.forClient;
          return (
            <TooltipBubble
              title={opportunity}
              disabled={!opportunity}
              placement="top-start"
            >
              <div className="truncate text-ds-natural-600">
                <TextBase
                  label={opportunity}
                  color="text-ds-natural-600"
                />
              </div>
            </TooltipBubble>
          );
        },
      },
      {
        title: (
          <div className="flex items-center justify-start">
            <TextCaption
              label="Creation source"
              color="text-ds-natural-300"
            />
            <div className="pl-1.5">
              <div ref={infoRef}>
                <InfoOutlineIcon
                  size={16}
                  color={ColorHex.natural}
                />
              </div>
              <Tooltip
                placement="top"
                anchorRef={infoRef}
                trigger="mouseenter"
                autoCalcArrowPosition
                arrowDown
              >
                <div className="w-36 p-2.5">
                  <TextCaption
                    label="Sandboxes can be created from a presentation or demo."
                    color="text-ds-natural-600"
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        ),
        width: 135,
        maxContentWidth: 116,
        render: () => (
          <TooltipBubble
            title={demo.demoName}
            placement="top-start"
          >
            <div className="truncate text-ds-natural-600">
              <TextBase
                label={demo.demoName}
                color="text-ds-natural-600"
              />
            </div>
          </TooltipBubble>
        ),
      },
      {
        title: 'Link type',
        alignment: 'center',
        width: 87,
        render: (link) => {
          const level = link.options.security.level;
          const Icon = accessIcons[level];
          const tooltipText = accessDescription[level];

          return (
            <TooltipBubble
              title={tooltipText}
              placement="top"
            >
              <div className="text-ds-natural-300">
                <Icon size={18} />
              </div>
            </TooltipBubble>
          );
        },
      },
      {
        title: 'Status',
        alignment: 'center',
        width: 72,
        render: (link) => (
          <LinkToggle
            link={link}
            onToggle={toggleActive}
          />
        ),
      },
      {
        title: 'Expires in',
        width: 82,
        render: ({ options }) => {
          const { expiresAt } = options;
          const expired = expiresAt && !isFuture(expiresAt);
          const label = !expiresAt
            ? '-'
            : expired
              ? 'Expired'
              : dateUTCFromNow(dayjs.unix(expiresAt), true);

          return (
            <TooltipBubble
              title={
                expiresAt &&
                `${!expired ? 'Expires on' : 'Expired on'} ${MMMDoYYYYUnix(expiresAt)}`
              }
              placement="top-start"
              disabled={!expiresAt}
            >
              <TextCaption label={label} />
            </TooltipBubble>
          );
        },
      },
      {
        title: 'Visited',
        alignment: 'right',
        width: 36,
        render: (link) => {
          const visits = link.numberOfAccesses;
          return (
            <TooltipBubble
              title={handleSinglePlural(visits, 'Visited')}
              placement="top"
            >
              <TextBase
                label={visits || '-'}
                color="text-ds-natural-600"
              />
            </TooltipBubble>
          );
        },
      },
      {
        title: '',
        alignment: 'right',
        width: 160,
        render: (link) => (
          <LinkActions
            onSubmit={handleSubmit}
            link={link}
            isLoading={link.id === loadingLinkId}
          />
        ),
      },
    ],
    [demo.demoName, handleSubmit, toggleActive, loadingLinkId]
  );

  const fetchArgs = useMemo(
    () => ({
      organizationId,
      id: demoId,
      sort,
      workspaces: [workspaceId],
      creators: creators?.split(','),
      companies: companies?.split(','),
      sources: sources?.split(','),
      query,
    }),
    [
      organizationId,
      workspaceId,
      demoId,
      sort,
      creators,
      companies,
      sources,
      query,
    ]
  );

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchFirstBatch, fetchArgs]);

  return (
    <>
      {!isLoading && !items?.length ? (
        <EmptyState
          title="No shares yet"
          description="Once the demo is shared, you can manage its share settings from here"
          action={
            <Button
              link
              label="Share demo"
              leftIcon={<ShareOutlineIcon />}
              onClick={() => {
                NiceModal.show(ShareDemoFlow, {
                  workspaceId,
                  goToShares: goToLinksTab,
                  demoId: Number(demoId),
                });
              }}
            />
          }
        />
      ) : (
        <div className="py-5">
          <FixedTable<DemoShare>
            sideSpacing
            rowWrapper={TableRowWrapper}
            cols={cols}
            data={items}
            handleFetchNextBatch={() => {
              fetchNextBatch(fetchArgs);
            }}
            infiniteListMaxHeight={600}
            isLoading={isLoading}
            isLoadingBatch={isLoadingBatch}
            error={error}
            loader={
              <Container>
                <div className="w-ds-lg-modal">
                  <SkeletonRowLoading
                    medium
                    itemsCount={5}
                  />
                </div>
              </Container>
            }
          />
        </div>
      )}
    </>
  );
};
